import {
  CompanyIntake,
  CompanyIntakeBusiness,
  CompanyIntakeCulture,
  CompanyIntakeGeneral,
  CompanyIntakeSales,
  CompanyIntakeSite,
  CompanyIntakeTab,
  CompanyIntakeTalentRecruitingHistoryExternal,
  CompanyIntakeTalentRecruitingHistoryGeneral,
  CompanyIntakeTalentRecruitingHistoryInternal,
  Stakeholder,
} from "@/types";

// Job-intake progress is no longer computed here — it's served by the BE
// via `useGetJobIntake`'s `intakeCompletion` response. See
// guac/src/admin-api/admin-job-intake/intake-progress.util.ts.
// This module now only covers company-intake (and the shared Stakeholder
// helper used by the company-intake UI).

// ---------------------------------------------------------------------------
// Type-safe, data-driven field registries
//
// Each registry maps *every* key of a type to a FieldDef. TypeScript errors
// if a new field is added to a type but missing from its registry.
//
// "tracked" fields are accessed by their object key at runtime, so both the
// total count and the filled count are derived from the same definition —
// they can never drift apart.
// ---------------------------------------------------------------------------

export interface Progress {
  filled: number;
  total: number;
}

enum FieldType {
  /** Field contributes to progress (counted in total & filled) */
  Tracked = "tracked",
  /** Field is intentionally skipped (booleans, IDs, timestamps) */
  Excluded = "excluded",
  /** Field is only counted when a gate condition is met */
  Conditional = "conditional",
  /** Field expands into multiple sub-fields for counting */
  Expanded = "expanded",
}

type FieldDef<T> =
  | FieldType.Tracked
  | FieldType.Excluded
  | {
      type: FieldType.Conditional;
      get: (obj: T) => unknown;
      gate: (obj: T) => boolean;
    }
  | { type: FieldType.Expanded; getters: ((obj: T) => unknown)[] };

type FieldRegistry<T> = Record<keyof T, FieldDef<T>>;

// ---------------------------------------------------------------------------
// Field registries — Company
// ---------------------------------------------------------------------------

const STAKEHOLDER_FIELDS: FieldRegistry<Stakeholder> = {
  // Tracked
  firstName: FieldType.Tracked,
  lastName: FieldType.Tracked,
  title: FieldType.Tracked,
  // Excluded (contact info may not be available for every stakeholder)
  phone: FieldType.Excluded,
  email: FieldType.Excluded,
  linkedinUrl: FieldType.Excluded,

  role: FieldType.Tracked,
  personalityAndNotes: FieldType.Tracked,
  communicationMode: FieldType.Tracked,
  communicationStyle: FieldType.Tracked,
  communicationFrequency: FieldType.Tracked,
  communicationDetails: FieldType.Tracked,
  valuePerception: FieldType.Tracked,
  valuedCandidateQualities: FieldType.Tracked,
  commonRejectionReasons: FieldType.Tracked,
  viewOnExternalPartners: FieldType.Tracked,

  // Excluded (booleans / metadata)
  id: FieldType.Excluded,
  companyId: FieldType.Excluded,
  primaryContact: FieldType.Excluded,
  ccOnEmails: FieldType.Excluded,
  createdAt: FieldType.Excluded,
  updatedAt: FieldType.Excluded,
};

const COMPANY_SALES_FIELDS: FieldRegistry<CompanyIntakeSales> = {
  details: FieldType.Tracked,
  dealTeam: FieldType.Tracked,
  salesOwner: FieldType.Tracked,
};

const COMPANY_GENERAL_FIELDS: FieldRegistry<CompanyIntakeGeneral> = {
  websiteUrl: FieldType.Tracked,
  industries: FieldType.Tracked,
  headCount: FieldType.Tracked,
  whatTheyMake: FieldType.Tracked,
  rawMaterials: FieldType.Tracked,
  equipment: FieldType.Tracked,
  manufacturingProcess: FieldType.Tracked,
  maintenanceEnvironment: FieldType.Tracked,
  customers: FieldType.Tracked,
  standards: FieldType.Tracked,
  // Not required for progress — optional enrichment
  mediaLinks: FieldType.Excluded,
};

const COMPANY_SITE_FIELDS: FieldRegistry<CompanyIntakeSite> = {
  siteName: FieldType.Tracked,
  address: FieldType.Tracked,
  city: FieldType.Tracked,
  state: FieldType.Tracked,
  zipCode: FieldType.Tracked,
  headCount: FieldType.Tracked,
  partnershipStartDate: FieldType.Tracked,
  notes: FieldType.Tracked,
  roleTypeBreakdown: {
    type: FieldType.Expanded,
    getters: [
      (s) => s.roleTypeBreakdown?.production,
      (s) => s.roleTypeBreakdown?.skilledTrades,
      (s) => s.roleTypeBreakdown?.professional,
    ],
  },
  employmentTypeBreakdown: {
    type: FieldType.Expanded,
    getters: [
      (s) => s.employmentTypeBreakdown?.temp,
      (s) => s.employmentTypeBreakdown?.contract,
      (s) => s.employmentTypeBreakdown?.fullTime,
    ],
  },
  // Excluded (metadata)
  id: FieldType.Excluded,
};

const COMPANY_TRH_GENERAL_FIELDS: FieldRegistry<CompanyIntakeTalentRecruitingHistoryGeneral> =
  {
    retention: FieldType.Tracked,
    hiringChallenges: FieldType.Tracked,
    competitors: FieldType.Tracked,
    details: FieldType.Tracked,
    currentlyUsingStaffingFirms: FieldType.Excluded,
    historicallyUsedStaffingFirms: FieldType.Excluded,
  };

const COMPANY_TRH_INTERNAL_FIELDS: FieldRegistry<CompanyIntakeTalentRecruitingHistoryInternal> =
  {
    hrTeamSize: FieldType.Tracked,
    currentHrBandwidthOnRecruiting: FieldType.Tracked,
    idealHrBandwidthOnRecruiting: FieldType.Tracked,
    details: FieldType.Tracked,
  };

const COMPANY_TRH_EXTERNAL_FIELDS: FieldRegistry<CompanyIntakeTalentRecruitingHistoryExternal> =
  {
    details: FieldType.Tracked,
    staffingFirmCostStructure: FieldType.Tracked,
    staffingFirmCostDetails: FieldType.Tracked,
    likesAboutExternalRecruiters: FieldType.Tracked,
    dislikesAboutExternalRecruiters: FieldType.Tracked,
  };

const COMPANY_CULTURE_FIELDS: FieldRegistry<CompanyIntakeCulture> = {
  companyCultureDetails: FieldType.Tracked,
  workplaceEnvironmentDetails: FieldType.Tracked,
};

const COMPANY_BUSINESS_FIELDS: FieldRegistry<CompanyIntakeBusiness> = {
  growthStatus: FieldType.Tracked,
  headcountTrend: FieldType.Tracked,
  revenueAndHeadcountDetails: FieldType.Tracked,
  details: FieldType.Tracked,
};

// ---------------------------------------------------------------------------
// Grace — stakeholders are considered complete when within this many fields
// of fully filled (e.g. 12/15 = complete). Exported for tooltip display.
// ---------------------------------------------------------------------------

export const STAKEHOLDER_GRACE = 0;
export const STAKEHOLDER_TOTAL_FIELDS = 12; // tracked fields in STAKEHOLDER_FIELDS

// ---------------------------------------------------------------------------
// Generic progress computation
// ---------------------------------------------------------------------------

function isFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    // Rich text editors (TipTap) produce "<p></p>" or "<p><br></p>" for empty content
    const EMPTY_RICH_TEXT = /<p>(\s|<br\s*\/?>)*<\/p>/gi;
    const stripped = value.replace(EMPTY_RICH_TEXT, "").trim();
    return stripped.length > 0;
  }

  if (typeof value === "number") return true;

  if (Array.isArray(value)) return value.length > 0;

  return true;
}

function computeProgress<T>(
  registry: FieldRegistry<T>,
  obj: T | null | undefined,
): Progress {
  // Backend may return a null/empty sub-object even when the intake row
  // exists — treat it as "present but unfilled" so total stays correct.
  const safeObj = (obj ?? {}) as T;

  let filled = 0;
  let total = 0;

  for (const [key, def] of Object.entries(registry)) {
    const typedDef = def as FieldDef<T>;

    if (typedDef === FieldType.Tracked) {
      total++;
      if (isFieldFilled((safeObj as Record<string, unknown>)[key])) filled++;
    } else if (typedDef === FieldType.Excluded) {
      // intentionally skipped
    } else if (typedDef.type === FieldType.Conditional) {
      if (typedDef.gate(safeObj)) {
        total++;
        if (isFieldFilled(typedDef.get(safeObj))) filled++;
      }
      // Expanded fields are handled below
    } else {
      total += typedDef.getters.length;
      filled += typedDef.getters.filter((get) =>
        isFieldFilled(get(safeObj)),
      ).length;
    }
  }

  return { filled, total };
}

function computePerItemTotal<T>(registry: FieldRegistry<T>): number {
  let total = 0;
  for (const def of Object.values(registry)) {
    const typedDef = def as FieldDef<T>;
    if (typedDef === FieldType.Tracked) total++;
    else if (
      typeof typedDef === "object" &&
      typedDef.type === FieldType.Expanded
    )
      total += typedDef.getters.length;
  }
  return total;
}

function computeArrayProgress<T>(
  registry: FieldRegistry<T>,
  items: T[] | null | undefined,
  grace = 0,
): Progress {
  const list = items ?? [];
  const itemTotal = computePerItemTotal(registry);
  const effectiveTotal = grace > 0 ? itemTotal - grace : itemTotal;
  const count = Math.max(list.length, 1);
  const total = count * effectiveTotal;

  let filled = 0;
  for (const item of list) {
    const itemFilled = Math.min(
      computeProgress(registry, item).filled,
      effectiveTotal,
    );
    filled += itemFilled;
  }

  return { filled, total };
}

function sumProgress(...parts: Progress[]): Progress {
  let filled = 0;
  let total = 0;
  for (const p of parts) {
    filled += p.filled;
    total += p.total;
  }
  return { filled, total };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function getCompanyProgress(
  companyIntake: CompanyIntake | null | undefined,
  stakeholders: Stakeholder[] | null | undefined,
): Progress {
  const trh = companyIntake?.talentRecruitingHistory;
  return sumProgress(
    computeProgress(COMPANY_SALES_FIELDS, companyIntake?.sales),
    computeProgress(COMPANY_GENERAL_FIELDS, companyIntake?.general),
    computeProgress(COMPANY_TRH_GENERAL_FIELDS, trh?.general),
    computeProgress(COMPANY_TRH_INTERNAL_FIELDS, trh?.internal),
    computeProgress(COMPANY_TRH_EXTERNAL_FIELDS, trh?.external),
    computeProgress(COMPANY_CULTURE_FIELDS, companyIntake?.culture),
    computeProgress(COMPANY_BUSINESS_FIELDS, companyIntake?.business),
    computeArrayProgress(STAKEHOLDER_FIELDS, stakeholders, STAKEHOLDER_GRACE),
    computeArrayProgress(COMPANY_SITE_FIELDS, companyIntake?.sites),
  );
}

function getCompanyTabProgress(
  companyIntake: CompanyIntake | null | undefined,
  stakeholders: Stakeholder[] | null | undefined,
): Record<CompanyIntakeTab, Progress> {
  const trh = companyIntake?.talentRecruitingHistory;
  return {
    [CompanyIntakeTab.SalesTeam]: computeProgress(
      COMPANY_SALES_FIELDS,
      companyIntake?.sales,
    ),
    [CompanyIntakeTab.Stakeholders]: computeArrayProgress(
      STAKEHOLDER_FIELDS,
      stakeholders,
      STAKEHOLDER_GRACE,
    ),
    [CompanyIntakeTab.GeneralInfo]: computeProgress(
      COMPANY_GENERAL_FIELDS,
      companyIntake?.general,
    ),
    [CompanyIntakeTab.Sites]: computeArrayProgress(
      COMPANY_SITE_FIELDS,
      companyIntake?.sites,
    ),
    [CompanyIntakeTab.TalentRecruitingHistory]: sumProgress(
      computeProgress(COMPANY_TRH_GENERAL_FIELDS, trh?.general),
      computeProgress(COMPANY_TRH_INTERNAL_FIELDS, trh?.internal),
      computeProgress(COMPANY_TRH_EXTERNAL_FIELDS, trh?.external),
    ),
    [CompanyIntakeTab.Culture]: computeProgress(
      COMPANY_CULTURE_FIELDS,
      companyIntake?.culture,
    ),
    [CompanyIntakeTab.Business]: computeProgress(
      COMPANY_BUSINESS_FIELDS,
      companyIntake?.business,
    ),
  };
}

function getStakeholderProgress(stakeholder: Stakeholder): Progress {
  return computeProgress(STAKEHOLDER_FIELDS, stakeholder);
}

function getSiteProgress(site: CompanyIntakeSite): Progress {
  return computeProgress(COMPANY_SITE_FIELDS, site);
}

export const intake = {
  getCompanyProgress,
  getCompanyTabProgress,
  getSiteProgress,
  getStakeholderProgress,
};
